<!--
  //nuxt-ui/components/pages/teams-team/TeamSeasonsFilter.vue
  -->

<script setup lang="ts">
import type { Season } from "~/src/season-cluster/domain/models/01.Season";
import type { RegionOption } from "~/src/region/domain/models/Region";
import { useSeasonFilter } from "~/nuxt-ui/Shared/composables/season/seasonFilter";
import { useTeams } from "~/nuxt-ui/Shared/composables/teams";
import { useWebContext } from "~/nuxt-ui/Shared/composables/webContext";
import RegionUiService from "~/src/region/infrastructure/ui-services/regionUiService";

type Props = {
  combinedWithMoreFilters?: boolean;
  teamId: number;
  teamSeasons: Season[] | undefined;
  initialFilterValues?: { seasonId: number; seasonClusterId: number; regionId: number };
};

const props = withDefaults(defineProps<Props>(), {
  combinedWithMoreFilters: false,
});

const emit = defineEmits(["change"]);

const { changeSeason } = useSeasonFilter();
const { findTeamCompetitionsIds } = useTeams();
const { findRegionInfoByRegionId, findRegionInfoByCompetitionId } = RegionUiService();
const { webContext } = useWebContext();

const regionOptions: RegionOption[] = props.teamSeasons
  ? await findTeamCompetitionsIds(props.teamSeasons)
  : [];
const teamSeasons = props.teamSeasons || ([] as Season[]);

const regionId = defineModel<number>("regionId");
const seasonClusterId = defineModel<number>("seasonClusterId");
const seasonId = defineModel<number>("seasonId");

const seasonOptions = computed((): Season[] => {
  return teamSeasons
    .filter(s => {
      const regionCompetitions = findRegionInfoByRegionId(regionId.value, "competitionId");
      return (
        regionCompetitions &&
        regionCompetitions.includes(s.competitionId) &&
        s.seasonClusterId === seasonClusterId.value &&
        s.competition.visible
      );
    })
    .sort((a, b) => {
      return a.start > b.start ? 1 : a.start < b.start ? -1 : 0;
    });
});

const { data: seasonClustersAvailable } = await useFetch("/api/v1/competition/season-clusters/available");

const seasonClusterOptions = computed((): { name: string; id: number }[] => {
  const seasonClusterIds = [...new Set(teamSeasons.map(s => s.seasonClusterId))];
  const options =
    seasonClustersAvailable.value.filter(
      cluster => seasonClusterIds.includes(cluster.id) && cluster.competitionIds.includes(regionId.value)
    ) || [];
  const simplifiedOptions = options.map(o => {
    return { name: o.name, id: o.id };
  });

  return options.length ? simplifiedOptions : undefined;
});

const handleSeason = (seasonClusterId: number, seasonIdArg: number) => {
  changeSeason(seasonClusterId, seasonIdArg, regionId.value, props.initialFilterValues);
  seasonId.value = seasonIdArg;
  emit("change", seasonId.value);
};

watchEffect(() => {
  for (let i = 0; i <= seasonClustersAvailable.value.length - 1; i++) {
    const cluster = seasonClustersAvailable.value[i];

    const matchingSeasons = teamSeasons.filter(
      season =>
        season.seasonClusterId === cluster.id &&
        findRegionInfoByCompetitionId(season.competitionId, "id") === regionId.value
    );

    if (matchingSeasons.length > 0) {
      seasonClusterId.value = cluster.id;
    }
  }
});

watch([regionId, seasonClusterId], ([newRegion, newSeasonCluster], [prevRegion, prevSeasonCluster]) => {
  if (newRegion !== prevRegion || newSeasonCluster !== prevSeasonCluster) {
    handleSeason(seasonClusterId.value, seasonOptions.value[seasonOptions.value.length - 1].id);
  }
});
</script>

<template>
  <div class="flex flex-col gap-2 md:flex-row md:flex-end justify-end my-6">
    <div
      v-if="seasonOptions.length"
      :class="['flex rounded-md shadow-sm justify-end', combinedWithMoreFilters ? 'max-w-max' : '']"
    >
      <!--    Seasons -->
      <button
        v-for="(season, index) in seasonOptions"
        :key="index"
        @click="handleSeason(season.seasonClusterId, season.id)"
        :class="[
          'kql-season-button',
          {
            '!bg-primary !text-black': season.id === seasonId,
          },
        ]"
      >
        {{ season.displayName }}
      </button>

      <!-- Clusters -->
      <select
        id="seasonCluster"
        v-model="seasonClusterId"
        class="kql-select text-[10px] bigsm:text-xs lg:text-sm border-l rounded-e-lg min-w-min lg:min-w-28"
      >
        <option
          v-for="seasonCluster in seasonClusterOptions"
          :key="seasonCluster.id"
          :value="seasonCluster.id"
        >
          {{ seasonCluster.name }}
        </option>
      </select>
    </div>

    <div v-else class="flex px-4 lg:px-0 shadow-sm justify-end">
      <div class="h-10 w-full max-w-xl bg-gray-800 rounded-lg animate-pulse"></div>
    </div>

    <!--    Regions-->
    <select
      v-if="!!regionId && regionOptions.length > 0"
      id="regionOptions"
      class="kql-select order-first md:order-last md:ml-6 text-[10px] bigsm:text-xs lg:text-sm border-l rounded-lg min-w-32"
      v-model="regionId"
    >
      <option v-for="(region, index) in regionOptions" :key="index" :value="region.id">
        {{ region.name }}
      </option>
    </select>
  </div>
</template>

<style scoped></style>
